/**
 * 历史消息 Hook
 * 负责加载和管理历史消息
 * 数据存储在 Zustand store 中，支持多会话缓存
 */
import { useCallback, useState } from 'react';
import wsuiApi from '@api/wsuiApi';
import type { IMessage, IToolTip } from '@typing/wsui/getMessages';
import type { MixMsgItem, ImFlowItem, ImFlowContentItem } from '@typing/wsui/msg';
import { useChatStore } from '@store/chatStore';

interface UseHistoryMessagesOptions {
    sessionId?: string;
    shareId?: string;
}

// 模块级稳定空数组
const EMPTY_MESSAGES: IMessage[] = [];

function parseFeatureToToolTip(feature?: ImFlowContentItem['feature'] & { attributeStatus?: number }): IToolTip {
    const tools: Array<{ type: 'like' | 'unlike' | 'copy'; status?: number; popupConfig?: unknown }> = [];

    if (feature?.copyInfo?.enable) {
        tools.push({ type: 'copy' });
    }
    if (feature?.like) {
        tools.push({
            type: 'like',
            status: (feature as any).attributeStatus === 1 ? 1 : 0,
            popupConfig: feature.like.actionInfo?.interactionInfo?.popupInfo?.content,
        });
    }
    if (feature?.dislike) {
        tools.push({
            type: 'unlike',
            status: (feature as any).attributeStatus === 2 ? 1 : 0,
            popupConfig: feature.dislike.actionInfo?.interactionInfo?.popupInfo?.content,
        });
    }

    return (tools.length > 0 ? { tools } : {}) as IToolTip;
}

function parseReferenceFeatureToToolTip(
    feature?: ImFlowContentItem['feature'],
    fallbackFragmentId?: string
): IToolTip & { fragment_id?: string } {
    const toolTip = parseFeatureToToolTip(feature) as IToolTip & { fragment_id?: string };
    const fragmentId =
        feature?.like?.actionInfo?.interactionInfo?.popupInfo?.content?.submitBtn?.interactionInfo?.params
            ?.fragmentId ||
        feature?.dislike?.actionInfo?.interactionInfo?.popupInfo?.content?.submitBtn?.interactionInfo?.params
            ?.fragmentId ||
        fallbackFragmentId;

    if (fragmentId) {
        toolTip.fragment_id = fragmentId;
    }

    return toolTip;
}

function normalizeReferencesData(contentReplace: unknown): unknown {
    if (!contentReplace || typeof contentReplace !== 'object') {
        return contentReplace;
    }

    const data = contentReplace as { references?: any[]; list?: any[] };
    const references = Array.isArray(data.references)
        ? data.references
        : Array.isArray(data.list)
          ? data.list
          : undefined;

    if (!references) {
        return contentReplace;
    }

    return {
        ...data,
        references: references.map((reference) => ({
            ...reference,
            tool_tip: reference.tool_tip || parseReferenceFeatureToToolTip(reference.feature, reference.id),
        })),
    };
}

function getSegmentType(component?: string): IMessage['sections'][number]['segments'][number]['type'] {
    if (component === 'thinkProcessV2') {
        return 'thinkProcessV2';
    }
    if (component === 'oeReferences') {
        return 'oeReferences';
    }
    if (component === 'appendAsk') {
        return 'appendAsk';
    }
    if (component === 'fileCard') {
        return 'fileCard';
    }
    if (component === 'mediaList') {
        return 'mediaList';
    }
    if (component === 'text') {
        return 'text';
    }
    return 'markdown';
}

/**
 * 将 msgData（Record<msgId, MixMsgItem>）转换为 IMessage[]
 * 按 sendTime 排序；仅处理 ImText（用户消息）和 ImFlow（bot 消息）
 */
export function convertMsgDataToMessages(msgData: Record<string, MixMsgItem>, msgIds?: string[]): IMessage[] {
    // 按 msgIds 顺序或 sendTime 排序
    const sortedIds =
        msgIds && msgIds.length > 0
            ? msgIds
            : Object.keys(msgData).sort(
                  (a, b) => Number(msgData[a]?.meta?.sendTime || 0) - Number(msgData[b]?.meta?.sendTime || 0)
              );

    const messages: IMessage[] = [];

    for (const id of sortedIds) {
        const item = msgData[id];
        if (!item) continue;

        const { meta, data } = item;
        const msgId = meta.msgId || id;

        if (meta.ownerType === 3) {
            // 用户消息：根据 cardName 分发处理（对齐 vita mix 页面 CardComponents componentsMap）
            // ImText(11402) → 纯文本，ImImage(11403) → 单图，ImRichMsg/ImRichText(11413) → 图文混排
            const cardName: string = data.content?.cardName || '';
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const contentData = (data.content?.data as any)?.content || {};
            let text: string = contentData.value || '';

            // 提取用户消息中的图片列表（对齐 vita ImImage + ImRichMsg 渲染逻辑）
            let images: IMessage['images'] = undefined;

            if (cardName === 'ImImage') {
                // 单图消息（cardId=11403）：value 为图片 URL，origin 为原图（对齐 vita convertImage msgUtils.ts 第64-101行）
                const imgOrigin = contentData.origin || contentData.value;
                if (imgOrigin) {
                    images = [
                        {
                            icon: contentData.value || imgOrigin,
                            origin: imgOrigin,
                            small: contentData.value || imgOrigin,
                        },
                    ];
                }
            } else if (cardName === 'ImRichMsg' || cardName === 'ImRichText') {
                // 图文混排消息（cardId=11413）：list 中 type='images' 的项携带图片数组（对齐 vita convertRichText msgUtils.ts 第103-157行）
                const richList: any[] = contentData.list || [];
                const extractedImages: NonNullable<IMessage['images']> = [];
                const textParts: string[] = [];
                for (const item of richList) {
                    if (item.type === 'images' && Array.isArray(item.images)) {
                        for (const img of item.images) {
                            if (img.origin || img.icon) {
                                extractedImages.push({
                                    icon: img.icon || img.small || img.origin,
                                    origin: img.origin,
                                    small: img.small || img.icon || img.origin,
                                    value: img.value,
                                });
                            }
                        }
                    } else if (item.type === 'text' && item.content) {
                        textParts.push(item.content);
                    }
                }
                if (extractedImages.length > 0) images = extractedImages;
                if (!text && textParts.length > 0) text = textParts.join('\n');
            }

            // 纯文本或图文消息都应保留（不能仅因无 text 就跳过，否则纯图片消息会丢失）
            if (!text && !images) continue;

            messages.push({
                message_id: msgId,
                poster: { name: '', avatar: '', role: 'user' },
                sections: text
                    ? [
                          {
                              section_idx: '0',
                              segments: [
                                  {
                                      segment_idx: '0',
                                      type: 'text',
                                      content: text,
                                      is_finished: true,
                                      tool_tip: {},
                                  },
                              ],
                              tool_tip: {},
                          },
                      ]
                    : [],
                images,
            });
        } else if (meta.ownerType === 2) {
            // bot 消息：仅处理 ImFlow（cardId: 11412）
            const cardName: string = data.content?.cardName || '';
            if (cardName !== 'ImFlow') continue;

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const contentObj = (data.content?.data as any)?.content;
            const list: ImFlowItem[] = contentObj?.list || [];
            const isInterrupted: boolean = contentObj?.isInterrupted;
            if (list.length === 0) continue;

            const sections = list
                .map((flowItem) => {
                    const sectionId = String(flowItem.sectionId);
                    const ids = flowItem.contentList?.ids || [];
                    const itemData = flowItem.contentList?.data || {};
                    const segments: IMessage['sections'][number]['segments'] = [];
                    let markdown = '';
                    let markdownStartKey = '';
                    let markdownToolTip: IToolTip = {};

                    const flushMarkdown = () => {
                        if (!markdown) return;
                        segments.push({
                            segment_idx: markdownStartKey || String(segments.length),
                            type: 'markdown',
                            content: markdown,
                            is_finished: true,
                            tool_tip: markdownToolTip,
                        });
                        markdown = '';
                        markdownStartKey = '';
                        markdownToolTip = {};
                    };

                    for (const key of ids) {
                        const contentItem = itemData[key];
                        const type = getSegmentType(contentItem?.component);

                        if (type === 'markdown') {
                            const content = contentItem?.contentAdd || '';
                            if (!content) continue;
                            if (!markdownStartKey) {
                                markdownStartKey = key;
                                markdownToolTip = parseFeatureToToolTip(contentItem?.feature);
                            }
                            markdown += content;
                            continue;
                        }

                        flushMarkdown();

                        if (type === 'thinkProcessV2' || type === 'oeReferences') {
                            const contentReplace = normalizeReferencesData(contentItem?.contentReplace);
                            if (contentReplace === undefined) continue;
                            segments.push({
                                segment_idx: key,
                                type,
                                content: JSON.stringify(contentReplace),
                                is_finished: true,
                                tool_tip: parseFeatureToToolTip(contentItem?.feature),
                            });
                            continue;
                        }

                        if (type === 'fileCard' || type === 'mediaList') {
                            const contentReplace = contentItem?.contentReplace;
                            if (contentReplace === undefined) continue;
                            segments.push({
                                segment_idx: key,
                                type,
                                content: JSON.stringify(contentReplace),
                                is_finished: true,
                                tool_tip: parseFeatureToToolTip(contentItem?.feature),
                            });
                            continue;
                        }

                        const content = contentItem?.contentAdd || '';
                        if (!content) continue;
                        segments.push({
                            segment_idx: key,
                            type,
                            content,
                            is_finished: true,
                            tool_tip: parseFeatureToToolTip(contentItem?.feature),
                        });
                    }

                    flushMarkdown();

                    if (segments.length === 0) return null;

                    return {
                        section_idx: sectionId,
                        segments,
                        tool_tip: {},
                    };
                })
                .filter(Boolean) as IMessage['sections'];

            if (sections.length === 0) continue;

            messages.push({
                message_id: msgId,
                poster: { name: 'AI助手', avatar: '', role: 'bot' },
                sections,
                stop_type: isInterrupted ? 'user_cancel' : undefined,
            });
        }
    }

    return messages;
}

export const useHistoryMessages = (options: UseHistoryMessagesOptions) => {
    const { sessionId, shareId } = options;

    const effectiveSessionId = sessionId || shareId || '';

    const {
        getSession,
        setMessages: storeSetMessages,
        setHasMore: storeSetHasMore,
        setPageTitle,
        prependMessages,
    } = useChatStore();

    const messages = useChatStore((state) => state.sessions[effectiveSessionId]?.messages ?? EMPTY_MESSAGES);
    const hasMore = useChatStore((state) => state.sessions[effectiveSessionId]?.hasMore ?? false);

    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string>('');

    /**
     * 加载历史消息
     */
    const loadHistoryMessages = useCallback(async () => {
        if (!sessionId && !shareId) {
            console.log('⚠️ 没有 sessionId 或 shareId，跳过历史消息加载');
            return;
        }

        const targetSessionId = sessionId || shareId || '';

        try {
            setLoading(true);
            setError('');
            const response = await wsuiApi.getMessages({
                sessionId: targetSessionId,
                size: 20,
                viewType: 'pre',
                businessType: 'doctor_youyi',
                appKey: 'pc_wenzhen',
            });

            if (response.errno === 0) {
                const converted = convertMsgDataToMessages(response.data.msgData, response.data.msgIds);
                storeSetMessages(targetSessionId, converted);
                storeSetHasMore(targetSessionId, response.data.hasMore);
                // 用第一条用户消息内容作为 pageTitle
                const existingTitle = useChatStore.getState().sessions[targetSessionId]?.pageTitle;
                if (!existingTitle || existingTitle === '对话详情') {
                    const firstUserMsg = converted.find((m) => m.poster?.role === 'user');
                    if (firstUserMsg) {
                        const textContent = firstUserMsg.sections?.[0]?.segments?.[0]?.content;
                        const imagePrefix = firstUserMsg.images?.length
                            ? '[图片]'.repeat(firstUserMsg.images.length)
                            : '';
                        const title = imagePrefix + (textContent || '') || '对话详情';
                        setPageTitle(targetSessionId, title);
                    }
                }
            } else {
                setError(response.errmsg || '加载消息失败');
            }
        } catch (err) {
            setError('网络错误，请稍后重试');
            console.error('加载消息失败:', err);
        } finally {
            setLoading(false);
        }
    }, [sessionId, shareId, storeSetMessages, storeSetHasMore, setPageTitle]);

    /**
     * 加载更多消息（上拉加载）
     */
    const loadMoreMessages = useCallback(async () => {
        if (!hasMore || loadingMore || messages.length === 0) return;

        const targetSessionId = sessionId || shareId || '';

        try {
            setLoadingMore(true);
            const response = await wsuiApi.getMessages({
                sessionId: targetSessionId,
                size: 20,
                viewType: 'pre',
                businessType: 'doctor_youyi',
                appKey: 'pc_wenzhen',
            });

            if (response.errno === 0) {
                const converted = convertMsgDataToMessages(response.data.msgData, response.data.msgIds);
                prependMessages(targetSessionId, converted);
                storeSetHasMore(targetSessionId, response.data.hasMore);
            }
        } catch (err) {
            console.error('加载更多失败:', err);
        } finally {
            setLoadingMore(false);
        }
    }, [sessionId, shareId, messages, hasMore, loadingMore, prependMessages, storeSetHasMore]);

    const resetMessages = useCallback(() => {
        setError('');
    }, []);

    return {
        messages,
        loading,
        loadingMore,
        hasMore,
        error,
        setError,
        loadHistoryMessages,
        loadMoreMessages,
        resetMessages,
    };
};
